import parse from 'html-react-parser';
import React from 'react';
import { useParams } from 'react-router-dom';
import TextWithCut from './TextWithCut';
import CatalogItemsGrid from './CatalogItemsGrid';
import GalleryRenderer from '../../helpers/renderer/galleryRenderer';
import InquiryFormRenderer from '../../helpers/renderer/inquiryFormRenderer';
import PosterRenderer from '../../helpers/renderer/posterRenderer';
import BackButtonRenderer from '../../helpers/renderer/backButtonRenderer';

const emptyPoster = (value) => !value || (typeof value === 'object' && !value.background && !value.backgroundVertical && !value.heading && !value.subheading);

function TemplateRows({ rows, mobileColumn, props, isMobile, titleFallback, children }) {
    rows = rows || [];
    mobileColumn = mobileColumn || [];
    props = props || {};
    const params = useParams();
    const currentSlug = params['*'];

    const renderProperty = (prop) => {
        const name = prop.name;

        if (prop.type === 'catalog') {
            // Item layout / elements-in-a-row are this page's own value now (not the
            // layout's), so this always lists whatever children this page actually has,
            // using its own item layout (if one was configured) for title/subtitle.
            const catalogValue = (props[name] && typeof props[name] === 'object') ? props[name] : {};
            const label = prop.show_name ? (
                <div style={{ fontSize: '0.85rem', color: '#777', marginBottom: '4px', textAlign: 'center' }}>{name}</div>
            ) : null;
            return (
                <div key={name} style={{ marginBottom: '16px', width: '100%' }}>
                    {label}
                    <CatalogItemsGrid
                        children={children}
                        rows={catalogValue.itemLayoutRows || []}
                        elementsInARow={catalogValue.elementsInARow}
                        currentSlug={currentSlug}
                    />
                </div>
            );
        }

        const value = props[name];
        if (value == null || value === "") return null;
        if (Array.isArray(value) && value.length === 0) return null;

        const label = prop.show_name ? (
            <div style={{ fontSize: '0.85rem', color: '#777', marginBottom: '4px', textAlign: 'center' }}>{name}</div>
        ) : null;

        if (prop.type === 'gallery') {
            // Pages saved before the 'image'->'gallery' rename may still hold a plain URL
            // string rather than an array of {src,title}; normalize before passing down.
            const images = Array.isArray(value) ? value : (typeof value === 'string' && value ? [{ src: value, title: '' }] : []);
            if (images.length === 0) return null;
            const comp = { type: 'gallery', images, ...(prop.meta || {}), altFallback: titleFallback };
            return (
                <div key={name} style={{ marginBottom: '16px', width: '100%' }}>
                    {label}
                    <GalleryRenderer comp={comp} />
                </div>
            );
        }

        if (prop.type === 'poster') {
            if (emptyPoster(value)) return null;
            return (
                <div key={name} style={{ marginBottom: '16px', width: '100%' }}>
                    {label}
                    <PosterRenderer comp={value} />
                </div>
            );
        }

        if (prop.type === 'line') {
            const fontSize = prop.meta.fontSize === 'heading' ? '2rem' : prop.meta.fontSize === 'sub-heading' ? '1.5rem' : '1rem';
            return (
                <div key={name} style={{ marginBottom: '16px', textAlign: 'center' }}>
                    {label}
                    <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, fontSize }}>{prop.meta.prefix} {value} {prop.meta.suffix}</div>
                </div>
            );
        }

        if (prop.type === 'text') {
            const html = (value && typeof value === 'object') ? value.rendered : value;
            const cut = prop.meta?.cut || 'none';
            return (
                <TextWithCut key={name} cut={cut} value={typeof html === 'string' ? parse(html) : html} label={label} />
            );
        }

        if (prop.type === 'widget') {
            if (!value.widgetId || !value.config) return null;
            const comp = { ...value.config, type: value.type, altFallback: titleFallback };
            if (value.type === 'back_button') {
                const parts = (currentSlug || '').split('/').filter(Boolean);
                if (parts.length <= 1) return null;
                comp.parentPath = parts.slice(0, -1).join('/');
                return (
                    <div key={name} style={{ marginBottom: '16px', width: '100%' }}>
                        {label}
                        <BackButtonRenderer comp={comp} />
                    </div>
                );
            }
            const renderer = value.type === 'inquiry_form' ? <InquiryFormRenderer comp={comp} />
                : value.type === 'poster' ? <PosterRenderer comp={comp} />
                : <GalleryRenderer comp={comp} />;
            return (
                <div key={name} style={{ marginBottom: '16px', width: '100%' }}>
                    {label}
                    {renderer}
                </div>
            );
        }

        if (prop.type === 'date') {
            const formatted = (() => {
                if (!value) return value;
                const [y, m, d] = value.split('-').map(Number);
                const date = new Date(y, m - 1, d);
                const month = date.toLocaleDateString('en-US', { month: 'short' });
                return `${month} ${date.getDate()}, ${date.getFullYear()}`;
            })();
            return (
                <div key={name} style={{ marginBottom: '16px', textAlign: 'center' }}>
                    {label}
                    <div style={{ lineHeight: 1.5 }}>{formatted}</div>
                </div>
            );
        }

        // default: 'line' or other simple types
        return (
            <div key={name} style={{ marginBottom: '12px', textAlign: 'center' }}>
                {label}
                <div>{value}</div>
            </div>
        );
    };

    if (rows.length === 0) return null;

    return (
        isMobile && mobileColumn.length > 0 ? (
            <div className="template-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px', width: '100%', alignItems: 'start' }}>
                {mobileColumn.map(renderProperty)}
            </div>
        ) : (
            <div className="template-rows" style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
                {rows.map((row, rIdx) => (
                    <div
                        key={rIdx}
                        className="template-grid"
                        style={{
                            display: 'grid',
                            gridTemplateColumns: (row.columns || []).map((c) => `${c.width || (100 / (row.columns.length || 1))}fr`).join(' '),
                            gap: '16px',
                            width: '100%',
                            alignItems: 'start',
                        }}
                    >
                        {(row.columns || []).map((col, cIdx) => (
                            <div key={cIdx} className="template-col" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                                {(col.components || []).map(renderProperty)}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        )
    );
}

export default TemplateRows;
